import { getRowerColorByKey } from '../../../../../utils/rowerColors';
import { PROFILE_STROKE_WIDTH, ROWER_DASH_PATTERNS } from './constants';
import type { DisplayLine, MetricMode, TracePoint, ViewMode } from './types';

export interface DisplayInfo {
  strokeLines: DisplayLine[];
  profileLines: DisplayLine[];
  notice?: string;
  emptyMessage?: string;
}

interface BuildDisplayInfoParams {
  rowerNames: string[];
  availableRowers: string[];
  viewMode: ViewMode;
  currentRower: string;
  strokesByRower: Record<string, TracePoint[][] | undefined>;
  profilesByRower: Record<string, TracePoint[] | undefined>;
  showStrokeOverlay: MetricMode;
  selectedCrewRowers: string[];
}

/** Compute the visual opacity for a trace line based on how recent it is (0 = oldest, 1 = newest). */
export const traceOpacity = (recency: number): number => 0.08 + 0.17 * recency;

/** Compute the stroke width for a trace line based on recency. */
export const traceStrokeWidth = (recency: number): number => 0.7 + 0.5 * recency;

/** Return the dash pattern for a rower based on the seat number embedded in the key. */
export const getRowerDashPattern = (rower: string): string | null => {
  const match = rower.match(/\d+/);
  const index = Math.max(0, (match ? parseInt(match[0], 10) : 1) - 1) % ROWER_DASH_PATTERNS.length;
  return ROWER_DASH_PATTERNS[index];
};

function thinStrokes(strokes: TracePoint[][]): { stroke: TracePoint[]; sourceIndex: number }[] {
  return strokes.map((stroke, sourceIndex) => ({ stroke, sourceIndex }));
}

function buildTraceLine(
  rower: string,
  sourceStrokeIndex: number,
  displayStrokeIndex: number,
  data: TracePoint[],
  totalDisplayed: number
): DisplayLine {
  const recency = totalDisplayed > 1 ? displayStrokeIndex / (totalDisplayed - 1) : 1;
  return {
    id: `${rower}-stroke-${sourceStrokeIndex}`,
    rower,
    label: `${rower.replace('Rower ', 'Seat ')} Stroke ${sourceStrokeIndex + 1}`,
    strokeIndex: sourceStrokeIndex,
    type: 'stroke',
    color: getRowerColorByKey(rower),
    opacity: traceOpacity(recency),
    strokeWidth: traceStrokeWidth(recency),
    data,
  };
}

function buildProfileLine(rower: string, data: TracePoint[]): DisplayLine {
  return {
    id: `${rower}-profile`,
    rower,
    label: `${rower.replace('Rower ', 'Seat ')} Profile`,
    strokeIndex: null,
    type: 'profile',
    color: getRowerColorByKey(rower),
    opacity: 1,
    strokeWidth: PROFILE_STROKE_WIDTH,
    data,
  };
}

export const buildDisplayInfo = ({
  rowerNames,
  availableRowers,
  viewMode,
  currentRower,
  strokesByRower,
  profilesByRower,
  showStrokeOverlay,
  selectedCrewRowers,
}: BuildDisplayInfoParams): DisplayInfo => {
  const strokeLines: DisplayLine[] = [];
  const profileLines: DisplayLine[] = [];
  let notice: string | undefined;
  let emptyMessage: string | undefined;

  if (!rowerNames.length) {
    return {
      strokeLines: [],
      profileLines: [],
      emptyMessage: 'Gate force data will appear here once uploaded.',
    };
  }

  if (viewMode === 'single-rower-all-strokes') {
    const strokes = strokesByRower[currentRower] || [];
    const displayStrokes = thinStrokes(strokes);
    displayStrokes.forEach(({ stroke, sourceIndex }, displayStrokeIndex) => {
      strokeLines.push(
        buildTraceLine(currentRower, sourceIndex, displayStrokeIndex, stroke, displayStrokes.length)
      );
    });
    if (!displayStrokes.length) {
      emptyMessage = `No stroke data available for ${currentRower}.`;
    }
  } else if (viewMode === 'rower-profile') {
    const strokes = strokesByRower[currentRower] || [];
    const profile = profilesByRower[currentRower] || [];
    const traces = showStrokeOverlay === 'profile' ? [] : thinStrokes(strokes);
    const includeProfile = showStrokeOverlay !== 'traces' && profile.length > 0;

    traces.forEach(({ stroke, sourceIndex }, displayStrokeIndex) => {
      strokeLines.push(
        buildTraceLine(currentRower, sourceIndex, displayStrokeIndex, stroke, traces.length)
      );
    });

    if (includeProfile) {
      profileLines.push(buildProfileLine(currentRower, profile));
    } else if (showStrokeOverlay !== 'traces' && !profile.length) {
      notice = 'Session profile not available for this rower.';
    }

    if (!strokeLines.length && !profileLines.length) {
      emptyMessage = `No data available for ${currentRower}.`;
    }
  } else if (viewMode === 'crew-profiles') {
    const activeRowers =
      selectedCrewRowers.length === 0
        ? availableRowers
        : selectedCrewRowers.filter((rower) => availableRowers.includes(rower));

    const datasets = activeRowers
      .map((rower) => {
        const profile = profilesByRower[rower];
        return profile && profile.length > 0 ? buildProfileLine(rower, profile) : null;
      })
      .filter(Boolean) as DisplayLine[];
    profileLines.push(...datasets);
    if (!datasets.length && activeRowers.length > 0) {
      notice = 'No session averages available for selected rowers.';
    }
  }

  return { strokeLines, profileLines, notice, emptyMessage };
};
